import { useState, useEffect, useCallback } from 'react';
import { api, formatCurrency, getCurrentMonth } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Trash2, Activity, ChevronLeft, ChevronRight, Banknote, Shield, ArrowDown, Minus, TrendingUp, TrendingDown, Settings2, Check, Users, Plus, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmDelete from '../../components/ConfirmDelete';

const Perso = () => {
  const [currentMonth, setCurrentMonth] = useState(getCurrentMonth());
  const [data, setData] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [annuel, setAnnuel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPrendreDialog, setShowPrendreDialog] = useState(false);
  const [montant, setMontant] = useState('');
  const [note, setNote] = useState('');
  const [taking, setTaking] = useState(false);
  const [caInput, setCaInput] = useState('');
  const [savingCa, setSavingCa] = useState(false);
  const [editPct, setEditPct] = useState(false);
  const [pctInput, setPctInput] = useState('');
  const [paiementsEmployes, setPaiementsEmployes] = useState([]);
  const [totalPaiementsEmployes, setTotalPaiementsEmployes] = useState(0);
  const [resumeEmployes, setResumeEmployes] = useState([]);
  const [showEmpDialog, setShowEmpDialog] = useState(false);
  const [empNom, setEmpNom] = useState('');
  const [empMontant, setEmpMontant] = useState('');
  const [empNote, setEmpNote] = useState('');
  const [empDate, setEmpDate] = useState('');
  const [empSaving, setEmpSaving] = useState(false);
  const [editingPaiement, setEditingPaiement] = useState(null);
  const [employesList, setEmployesList] = useState([]);
  const [annuelEmployes, setAnnuelEmployes] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const annee = currentMonth.slice(0, 4);
      const [prevRes, histRes, annuelRes, empRes, empAnnuelRes] = await Promise.all([
        api.get(`/perso/previsionnel-semaine/${currentMonth}`),
        api.get(`/perso/historique/${currentMonth}`),
        api.get(`/perso/annuel/${annee}`),
        api.get(`/perso/paiements-employes/${currentMonth}`),
        api.get(`/perso/paiements-employes-annuel/${annee}`),
      ]);
      setData(prevRes.data);
      setHistorique(histRes.data);
      setAnnuel(annuelRes.data);
      setCaInput(prevRes.data.ca_previsionnel ? String(prevRes.data.ca_previsionnel) : '');
      setPaiementsEmployes(empRes.data.paiements || []);
      setTotalPaiementsEmployes(empRes.data.total || 0);
      setResumeEmployes(empRes.data.par_employe || []);
      setAnnuelEmployes(empAnnuelRes.data);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  }, [currentMonth]);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);

  useEffect(() => {
    api.get('/salaries').then(r => {
      const noms = (r.data || []).map(s => s.nom || '').filter(Boolean);
      setEmployesList(noms);
    }).catch(() => {});
  }, []);

  // Merge unique employee names from past payments into autocomplete
  const allEmployeNames = [...new Set([...employesList, ...paiementsEmployes.map(p => p.employe)])];

  const openEmpDialog = (paiement = null) => {
    if (paiement) {
      setEditingPaiement(paiement);
      setEmpNom(paiement.employe);
      setEmpMontant(String(paiement.montant));
      setEmpNote(paiement.note || '');
      setEmpDate(paiement.date || '');
    } else {
      setEditingPaiement(null);
      setEmpNom('');
      setEmpMontant('');
      setEmpNote('');
      setEmpDate(new Date().toISOString().slice(0, 10));
    }
    setShowEmpDialog(true);
  };

  const handleSaveEmpPaiement = async () => {
    const nom = empNom.trim();
    const mt = parseFloat(empMontant);
    if (!nom) { toast.error("Nom de l'employe requis"); return; }
    if (!mt || mt <= 0) { toast.error('Montant invalide'); return; }
    setEmpSaving(true);
    try {
      if (editingPaiement) {
        await api.put(`/perso/paiement-employe/${editingPaiement.id}`, {
          employe: nom, montant: mt, note: empNote, date: empDate,
        });
        toast.success('Paiement modifie');
      } else {
        await api.post('/perso/paiement-employe', {
          employe: nom, montant: mt, note: empNote, mois: currentMonth, date: empDate,
        });
        toast.success(`${formatCurrency(mt)} enregistre pour ${nom}`);
      }
      setShowEmpDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setEmpSaving(false);
  };

  const handleDeleteEmpPaiement = async (id) => {
    try { await api.delete(`/perso/paiement-employe/${id}`); toast.success('Supprime'); fetchData(); }
    catch { toast.error('Erreur'); }
  };

  const handleSaveCa = async () => {
    const v = parseFloat(caInput);
    if (!v || v <= 0) { toast.error('Montant invalide'); return; }
    setSavingCa(true);
    try {
      await api.put('/perso/previsionnel', { mois: currentMonth, ca_previsionnel: v });
      toast.success('Previsionnel enregistre');
      fetchData();
    } catch { toast.error('Erreur'); }
    setSavingCa(false);
  };

  const handleSavePct = async () => {
    const v = parseFloat(pctInput);
    if (!v || v < 1 || v > 100) { toast.error('Entre 1 et 100%'); return; }
    try {
      await api.put('/perso/settings', { pourcentage: v });
      toast.success(`${v}% enregistre`);
      setEditPct(false);
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handlePrendre = async () => {
    const m = parseFloat(montant);
    if (!m || m <= 0) { toast.error('Montant invalide'); return; }
    setTaking(true);
    try {
      await api.post('/perso/prendre', { montant: m, note, mois: currentMonth });
      toast.success(`Pris: ${formatCurrency(m)}`);
      setShowPrendreDialog(false);
      setMontant('');
      setNote('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setTaking(false);
  };

  const handleAnnuler = async (id) => {
    if (!window.confirm('Annuler ce prelevement ?')) return;
    try { await api.delete(`/perso/${id}`); toast.success('Annule'); fetchData(); }
    catch { toast.error('Erreur'); }
  };

  const navigateMonth = (dir) => {
    const [y, m] = currentMonth.split('-').map(Number);
    let newM = m + dir, newY = y;
    if (newM > 12) { newM = 1; newY++; }
    if (newM < 1) { newM = 12; newY--; }
    setCurrentMonth(`${newY}-${String(newM).padStart(2, '0')}`);
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 gap-4">
      <Activity size={28} className="animate-pulse-slow" style={{ color: 'rgb(var(--lm-gold))' }} />
      <span className="text-xs tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))]">Chargement</span>
    </div>
  );

  const pct = data?.pct_perso || 10;
  const hasPrev = data?.ca_previsionnel > 0;
  // Moins de 7 jours de CA reel → on reste sur le previsionnel, sinon on ajuste
  const assezDeData = (data?.jours_avec_ca || 0) >= 7;
  const parSemaine = hasPrev
    ? (assezDeData ? data.par_semaine_ajuste : data.par_semaine_prevu)
    : 0;
  const isPositive = parSemaine > 0;
  const ecart = data?.ecart_pct || 0;
  const budgetTotal = hasPrev
    ? (assezDeData ? data.budget_perso_ajuste : data.budget_perso_prevu) + (data?.report_mois_precedent || 0)
    : 0;
  const progressPct = budgetTotal > 0 ? Math.min(100, Math.max(0, ((data.total_pris + totalPaiementsEmployes) / budgetTotal) * 100)) : 0;

  return (
    <div className="space-y-5" data-testid="perso-page">
      {/* Header */}
      <div className="flex items-center justify-between animate-slide-up">
        <div className="flex items-center gap-2">
          <Shield size={14} style={{ color: 'rgb(var(--lm-gold) / 0.5)' }} />
          <span className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Espace prive</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigateMonth(-1)} className="p-1.5 rounded-sm hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))] transition-colors" data-testid="prev-month">
            <ChevronLeft size={16} />
          </button>
          <span className="font-title text-sm text-[rgb(var(--lm-text))] min-w-[120px] text-center" data-testid="current-month-label">
            {data?.mois_label || ''} {currentMonth.slice(0, 4)}
          </span>
          <button onClick={() => navigateMonth(1)} className="p-1.5 rounded-sm hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))] transition-colors" data-testid="next-month">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* 1. PREVISIONNEL CA — Input */}
      <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '40ms', animationFillMode: 'backwards' }} data-testid="previsionnel-input-card">
        <p className="text-[9px] tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Mon previsionnel CA HT</p>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            value={caInput}
            onChange={e => setCaInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSaveCa(); }}
            className="flex-1 bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-2xl h-14 text-center"
            placeholder="Ex: 45000"
            data-testid="ca-previsionnel-input"
          />
          <Button
            onClick={handleSaveCa}
            disabled={savingCa}
            className="h-14 px-5 bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-accent-text))]"
            data-testid="save-previsionnel-btn"
          >
            {savingCa ? '...' : 'OK'}
          </Button>
        </div>
        {/* % setting inline */}
        <div className="mt-2 flex items-center justify-center">
          {editPct ? (
            <span className="inline-flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
              <Input
                type="number"
                value={pctInput}
                onChange={e => setPctInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSavePct(); }}
                className="w-16 h-6 text-center text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono"
                min={1} max={100} autoFocus
                data-testid="pct-input"
              />
              <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">% des benefices</span>
              <button onClick={handleSavePct} className="p-0.5 rounded hover:bg-[rgb(var(--lm-surface-elevated))]" data-testid="pct-save">
                <Check size={12} style={{ color: 'rgb(var(--lm-green))' }} />
              </button>
            </span>
          ) : (
            <button
              onClick={() => { setEditPct(true); setPctInput(String(pct)); }}
              className="text-[9px] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] transition-colors flex items-center gap-1 cursor-pointer"
              data-testid="pct-edit-btn"
            >
              Je prends {pct}% des benefices <Settings2 size={9} />
            </button>
          )}
        </div>
      </div>

      {/* 2. CETTE SEMAINE — Big number */}
      {hasPrev && (
        <div className="lm-card overflow-hidden animate-slide-up" style={{ animationDelay: '80ms', animationFillMode: 'backwards' }} data-testid="weekly-budget-card">
          <div className="p-8 text-center">
            <p className="text-[10px] tracking-[0.4em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">
              {data.semaines_restantes > 0 ? 'Cette semaine vous pouvez prendre' : 'Mois termine — reste'}
            </p>
            <p className={`font-mono text-5xl md:text-6xl font-bold tracking-tight ${isPositive ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}
              data-testid="weekly-amount">
              {formatCurrency(Math.abs(parSemaine))}
            </p>

            {/* Ecart indicator — only show when enough data */}
            {assezDeData && ecart !== 0 && (
              <div className={`inline-flex items-center gap-1 mt-3 px-3 py-1 rounded-full text-xs font-mono ${ecart > 0 ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}
                data-testid="ecart-badge">
                {ecart > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {ecart > 0 ? '+' : ''}{ecart}% vs previsionnel
              </div>
            )}
            {/* Early month indicator */}
            {!assezDeData && data.jours_avec_ca > 0 && (
              <p className="text-[9px] text-[rgb(var(--lm-text-muted))] mt-2">
                Base sur votre previsionnel — ajustement apres 7j de CA
              </p>
            )}
            {!assezDeData && data.jours_avec_ca === 0 && (
              <p className="text-[9px] text-[rgb(var(--lm-text-muted))] mt-2">
                Base sur votre previsionnel
              </p>
            )}

            {/* Recap: pris / paye employes / reste / budget */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 max-w-md mx-auto">
              <div className="bg-[rgb(var(--lm-surface-elevated))]/50 rounded-xl p-3 text-center">
                <p className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1">Deja pris</p>
                <p className="font-mono text-lg font-bold text-[rgb(var(--lm-red))]">{formatCurrency(data.total_pris)}</p>
              </div>
              {totalPaiementsEmployes > 0 && (
                <div className="bg-[rgb(var(--lm-surface-elevated))]/50 rounded-xl p-3 text-center">
                  <p className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1">Employes</p>
                  <p className="font-mono text-lg font-bold text-orange-400">{formatCurrency(totalPaiementsEmployes)}</p>
                </div>
              )}
              <div className="bg-[rgb(var(--lm-surface-elevated))]/50 rounded-xl p-3 text-center">
                <p className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1">Reste</p>
                <p className="font-mono text-lg font-bold" style={{ color: 'rgb(var(--lm-green))' }}>{formatCurrency(Math.max(0, budgetTotal - data.total_pris - totalPaiementsEmployes))}</p>
              </div>
              <div className="bg-[rgb(var(--lm-surface-elevated))]/50 rounded-xl p-3 text-center">
                <p className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1">Budget</p>
                <p className="font-mono text-lg font-bold" style={{ color: 'rgb(var(--lm-gold))' }}>{formatCurrency(budgetTotal)}</p>
              </div>
            </div>

            {/* Progress bar */}
            {budgetTotal > 0 && (
              <div className="max-w-xs mx-auto mt-3">
                <div className="w-full h-1.5 rounded-full bg-[rgb(var(--lm-surface-elevated))] overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700 ease-out"
                    style={{
                      width: `${progressPct}%`,
                      background: progressPct > 80 ? 'rgb(var(--lm-red))' : progressPct > 50 ? 'rgb(var(--lm-gold))' : 'rgb(var(--lm-green))',
                    }} />
                </div>
              </div>
            )}

            {/* Prendre button */}
            <div className="mt-6">
              <Dialog open={showPrendreDialog} onOpenChange={setShowPrendreDialog}>
                <DialogTrigger asChild>
                  <Button className="px-8 py-2.5 bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-accent-text))] hover:opacity-90 text-sm" data-testid="prendre-btn">
                    <Banknote size={16} className="mr-2" /> Prendre
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))]">
                  <DialogHeader>
                    <DialogTitle className="font-title text-lg flex items-center gap-2">
                      <Banknote size={18} style={{ color: 'rgb(var(--lm-gold))' }} /> Prendre
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="text-center py-2">
                      <p className="text-[9px] tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))]">Budget semaine</p>
                      <p className="font-mono text-2xl mt-1" style={{ color: isPositive ? 'rgb(var(--lm-green))' : 'rgb(var(--lm-red))' }}>
                        {formatCurrency(Math.abs(parSemaine))}
                      </p>
                    </div>
                    <div>
                      <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-2">Montant</label>
                      <Input type="number" value={montant} onChange={e => setMontant(e.target.value)}
                        className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-2xl text-center h-14"
                        placeholder="0" autoFocus data-testid="prendre-montant" />
                    </div>
                    <div>
                      <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-2">Note (optionnel)</label>
                      <Input value={note} onChange={e => setNote(e.target.value)}
                        className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]"
                        placeholder="Courses, carburant..." data-testid="prendre-note" />
                    </div>
                    <Button onClick={handlePrendre} disabled={taking || !montant}
                      className="w-full bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-accent-text))] h-11"
                      data-testid="confirm-prendre">
                      {taking ? 'En cours...' : `Prendre ${montant ? formatCurrency(parseFloat(montant) || 0) : ''}`}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      )}

      {/* 3. DETAIL SEMAINES */}
      {hasPrev && data.semaines?.length > 0 && (
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '120ms', animationFillMode: 'backwards' }} data-testid="semaines-card">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[9px] tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))]">CA par semaine</p>
            <span className="text-[10px] font-mono text-[rgb(var(--lm-text-muted))]">
              Previsionnel: {formatCurrency(data.ca_previsionnel)} | Extrapole: {formatCurrency(data.ca_extrapole)}
            </span>
          </div>
          <div className="space-y-1.5">
            {data.semaines.map(s => {
              const isActive = s.status === 'en_cours';
              const isPast = s.status === 'passe';
              const target = data.ca_previsionnel / data.nb_semaines;
              const pctWeek = target > 0 ? Math.min(100, (s.ca_reel / target) * 100) : 0;
              return (
                <div key={s.semaine}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${isActive ? 'bg-[rgb(var(--lm-gold))/0.08] border border-[rgb(var(--lm-gold))/0.2]' : 'bg-[rgb(var(--lm-bg))]/30'}`}
                  data-testid={`semaine-${s.semaine}`}>
                  <span className={`text-xs font-mono w-6 ${isActive ? 'text-[rgb(var(--lm-gold))] font-bold' : 'text-[rgb(var(--lm-text-muted))]'}`}>
                    S{s.semaine}
                  </span>
                  <span className="text-[10px] text-[rgb(var(--lm-text-muted))] w-20">{s.debut}-{s.fin}</span>
                  <div className="flex-1">
                    {(isPast || isActive) ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-[rgb(var(--lm-surface-elevated))] rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-green-400 transition-all" style={{ width: `${pctWeek}%` }} />
                        </div>
                        <span className="font-mono text-[10px] text-green-400 w-16 text-right">{formatCurrency(s.ca_reel)}</span>
                      </div>
                    ) : (
                      <span className="text-[10px] text-[rgb(var(--lm-text-muted))] italic">a venir</span>
                    )}
                  </div>
                  {isActive && <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-[rgb(var(--lm-gold))/0.15] text-[rgb(var(--lm-gold))]">actuel</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 4. RÉSUMÉ CALCUL */}
      {hasPrev && (
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '160ms', animationFillMode: 'backwards' }}>
          <p className="text-[9px] tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Comment c'est calcule</p>
          <div className="space-y-2 text-xs">
            <SummaryRow label="Previsionnel CA HT" value={data.ca_previsionnel} positive />
            <SummaryRow label={`Achats estimes (${data.ratio_matieres || 30}%)`} value={-data.achats_estimes} />
            <SummaryRow label="Charges mensuelles" value={-data.charges} />
            <div className="border-t border-[rgb(var(--lm-border)/0.3)] pt-2">
              <SummaryRow label="Benefice prevu" value={Math.max(0, data.ca_previsionnel - (data.achats_estimes || 0) - data.charges)} highlight />
            </div>
            <SummaryRow label={`${pct}% = budget perso`} value={data.budget_perso_prevu} highlight />
            {assezDeData && (
              <>
                <div className="border-t border-[rgb(var(--lm-border)/0.3)] pt-2 mt-1">
                  <p className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1.5">Ajustement reel ({data.jours_avec_ca}j de CA)</p>
                  <SummaryRow label="CA extrapole (reel)" value={data.ca_extrapole} />
                  <SummaryRow label="Budget ajuste" value={data.budget_perso_ajuste} highlight />
                </div>
              </>
            )}
            {!assezDeData && data.jours_avec_ca > 0 && (
              <p className="text-[9px] text-[rgb(var(--lm-text-muted))] italic mt-1">
                Ajustement actif apres 7 jours de CA ({data.jours_avec_ca}/7)
              </p>
            )}
            {data.report_mois_precedent > 0 && (
              <div className="border-t border-[rgb(var(--lm-border)/0.3)] pt-2">
                <SummaryRow label="Report mois precedent" value={data.report_mois_precedent} positive />
              </div>
            )}
            {(data.total_pris > 0 || data.report_mois_precedent > 0 || totalPaiementsEmployes > 0) && (
              <div className="border-t border-[rgb(var(--lm-border)/0.3)] pt-2">
                {data.total_pris > 0 && <SummaryRow label="Deja pris" value={-data.total_pris} />}
                {totalPaiementsEmployes > 0 && <SummaryRow label="Paye employes (non declare)" value={-totalPaiementsEmployes} />}
                <SummaryRow label="Reste a prendre" value={data.reste_a_prendre} highlight />
              </div>
            )}
          </div>
        </div>
      )}

      {/* 5. HISTORIQUE PRELEVEMENTS */}
      <div className="animate-slide-up" style={{ animationDelay: '200ms', animationFillMode: 'backwards' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Banknote size={12} style={{ color: 'rgb(var(--lm-red))' }} />
            <p className="text-[9px] tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))]">
              Mes prelevements {data?.mois_label || ''}
            </p>
          </div>
          {historique.length > 0 && (
            <span className="font-mono text-sm font-bold text-[rgb(var(--lm-red))]">
              {formatCurrency(data?.total_pris || 0)}
            </span>
          )}
        </div>
        {historique.length === 0 ? (
          <div className="lm-card p-5 text-center">
            <Minus size={20} className="mx-auto mb-1.5 text-[rgb(var(--lm-text-muted)/0.3)]" />
            <p className="text-xs text-[rgb(var(--lm-text-muted))]">Aucun prelevement</p>
          </div>
        ) : (
          <div className="space-y-1">
            {historique.map((p) => (
              <div key={p.id} className="lm-card px-4 py-3 flex items-center justify-between group" data-testid={`prelevement-${p.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[rgb(var(--lm-red))]/10 flex items-center justify-center shrink-0">
                    <ArrowDown size={14} style={{ color: 'rgb(var(--lm-red))' }} />
                  </div>
                  <div>
                    <span className="font-mono text-base font-bold" style={{ color: 'rgb(var(--lm-red))' }}>{formatCurrency(p.montant)}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[rgb(var(--lm-text-muted))] font-mono">{p.date}</span>
                      {p.note && p.note !== 'via Telegram' && (
                        <span className="text-[10px] text-[rgb(var(--lm-text-secondary))]">{p.note}</span>
                      )}
                      {p.note === 'via Telegram' && (
                        <span className="text-[9px] px-1 py-0.5 rounded-sm" style={{ background: 'rgb(var(--lm-gold) / 0.06)', color: 'rgb(var(--lm-gold) / 0.6)' }}>TG</span>
                      )}
                    </div>
                  </div>
                </div>
                <button onClick={() => handleAnnuler(p.id)} className="opacity-0 group-hover:opacity-100 text-[rgb(var(--lm-red)/0.4)] hover:text-[rgb(var(--lm-red))] transition-all" data-testid={`annuler-${p.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 6. PAIEMENTS EMPLOYES NON DECLARES */}
      <div className="animate-slide-up" style={{ animationDelay: '220ms', animationFillMode: 'backwards' }} data-testid="paiements-employes-section">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users size={12} className="text-orange-400" />
            <p className="text-[9px] tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))]">
              Paye employes {data?.mois_label || ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {totalPaiementsEmployes > 0 && (
              <span className="text-[10px] font-mono text-orange-400">
                {formatCurrency(totalPaiementsEmployes)}
              </span>
            )}
            <button onClick={() => openEmpDialog()} className="p-1 rounded-sm hover:bg-[rgb(var(--lm-surface-elevated))] text-orange-400 transition-colors" data-testid="add-paiement-employe-btn">
              <Plus size={14} />
            </button>
          </div>
        </div>

        {/* Resume par employe */}
        {resumeEmployes.length > 0 && (
          <div className="lm-card p-4 mb-2" data-testid="resume-employes">
            <div className="space-y-1.5">
              {resumeEmployes.map(e => (
                <div key={e.employe} className="flex items-center justify-between">
                  <span className="text-xs text-[rgb(var(--lm-text-secondary))]">{e.employe}</span>
                  <span className="font-mono text-xs text-orange-400">{formatCurrency(e.total)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {paiementsEmployes.length === 0 ? (
          <div className="lm-card p-5 text-center">
            <Users size={20} className="mx-auto mb-1.5 text-[rgb(var(--lm-text-muted)/0.3)]" />
            <p className="text-xs text-[rgb(var(--lm-text-muted))]">Aucun paiement employe</p>
            <button onClick={() => openEmpDialog()} className="text-[10px] text-orange-400 hover:underline mt-1" data-testid="add-first-paiement">
              Ajouter un paiement
            </button>
          </div>
        ) : (
          <div className="space-y-1">
            {paiementsEmployes.map(p => (
              <div key={p.id} className="lm-card px-4 py-3 flex items-center justify-between group cursor-pointer hover:bg-[rgb(var(--lm-surface-elevated))/0.5] transition-colors"
                onClick={() => openEmpDialog(p)} data-testid={`paiement-emp-${p.id}`}>
                <div className="flex items-center gap-3">
                  <Users size={13} className="text-orange-400/50" />
                  <div>
                    <span className="text-sm font-medium text-[rgb(var(--lm-text))]">{p.employe}</span>
                    <span className="font-mono text-sm text-orange-400 ml-2">{formatCurrency(p.montant)}</span>
                    <span className="text-[10px] text-[rgb(var(--lm-text-muted))] ml-2 font-mono">{p.date}</span>
                    {p.note && <span className="text-[10px] text-[rgb(var(--lm-text-secondary))] ml-2">{p.note}</span>}
                  </div>
                </div>
                <ConfirmDelete onConfirm={() => handleDeleteEmpPaiement(p.id)} message="Supprimer ce paiement ?">
                  <span className="opacity-0 group-hover:opacity-100 text-[rgb(var(--lm-red)/0.4)] hover:text-[rgb(var(--lm-red))] transition-all" data-testid={`delete-emp-${p.id}`}>
                    <Trash2 size={13} />
                  </span>
                </ConfirmDelete>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dialog ajout/modif paiement employe */}
      <Dialog open={showEmpDialog} onOpenChange={setShowEmpDialog}>
        <DialogContent className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))]">
          <DialogHeader>
            <DialogTitle className="font-title text-lg flex items-center gap-2">
              <Users size={18} className="text-orange-400" />
              {editingPaiement ? 'Modifier paiement' : 'Payer un employe'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-2">Employe</label>
              <Input value={empNom} onChange={e => setEmpNom(e.target.value)}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]"
                placeholder="Nom ou extra" autoFocus list="employes-list"
                data-testid="emp-nom-input" />
              <datalist id="employes-list">
                {allEmployeNames.map(n => <option key={n} value={n} />)}
              </datalist>
            </div>
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-2">Montant</label>
              <Input type="number" value={empMontant} onChange={e => setEmpMontant(e.target.value)}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-2xl text-center h-14"
                placeholder="0" data-testid="emp-montant-input" />
            </div>
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-2">Date</label>
              <Input type="date" value={empDate} onChange={e => setEmpDate(e.target.value)}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]"
                data-testid="emp-date-input" />
            </div>
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-2">Note (optionnel)</label>
              <Input value={empNote} onChange={e => setEmpNote(e.target.value)}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]"
                placeholder="Ex: prime, extra..." data-testid="emp-note-input" />
            </div>
            <Button onClick={handleSaveEmpPaiement} disabled={empSaving || !empNom.trim() || !empMontant}
              className="w-full bg-orange-500 text-white hover:bg-orange-600 h-11"
              data-testid="save-emp-paiement-btn">
              {empSaving ? 'En cours...' : (editingPaiement ? 'Modifier' : `Enregistrer ${empMontant ? formatCurrency(parseFloat(empMontant) || 0) : ''}`)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 7. ANNUEL */}
      {annuel && (
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '240ms', animationFillMode: 'backwards' }}>
          <div className="flex items-center justify-between mb-4">
            <p className="text-[9px] tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))]">{annuel.annee}</p>
            <span className="font-mono text-sm" style={{ color: 'rgb(var(--lm-gold))' }}>{formatCurrency(annuel.total_annee)}</span>
          </div>
          <div className="flex items-end gap-1 h-20">
            {annuel.mois.map(m => {
              const maxVal = Math.max(...annuel.mois.map(x => x.total_pris), 1);
              const barH = m.total_pris > 0 ? Math.max(8, (m.total_pris / maxVal) * 100) : 0;
              const isActive = m.mois === currentMonth;
              return (
                <button key={m.mois} onClick={() => setCurrentMonth(m.mois)} className="flex-1 flex flex-col items-center justify-end gap-1 group cursor-pointer" data-testid={`month-btn-${parseInt(m.mois.split('-')[1])}`}>
                  {m.total_pris > 0 && (
                    <span className="text-[8px] font-mono text-[rgb(var(--lm-text-muted))] opacity-0 group-hover:opacity-100 transition-opacity">
                      {m.total_pris >= 1000 ? `${(m.total_pris / 1000).toFixed(1)}k` : Math.round(m.total_pris)}
                    </span>
                  )}
                  <div className="w-full rounded-t-sm transition-all duration-300" style={{
                    height: `${barH}%`,
                    minHeight: m.total_pris > 0 ? '4px' : '0',
                    background: isActive ? 'rgb(var(--lm-gold))' : m.total_pris > 0 ? 'rgb(var(--lm-gold) / 0.3)' : 'rgb(var(--lm-surface-elevated) / 0.3)',
                  }} />
                  <span className={`text-[8px] uppercase ${isActive ? 'font-medium' : ''}`} style={{ color: isActive ? 'rgb(var(--lm-gold))' : 'rgb(var(--lm-text-muted))' }}>
                    {m.mois_label.slice(0, 1)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 8. ANNUEL PAIEMENTS EMPLOYES */}
      {annuelEmployes && annuelEmployes.total_annee > 0 && (
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '280ms', animationFillMode: 'backwards' }} data-testid="annuel-employes-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users size={12} className="text-orange-400" />
              <p className="text-[9px] tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))]">Employes {annuelEmployes.annee}</p>
            </div>
            <span className="font-mono text-sm text-orange-400">{formatCurrency(annuelEmployes.total_annee)}</span>
          </div>

          {/* Bar chart par mois */}
          <div className="flex items-end gap-1 h-20 mb-4">
            {annuelEmployes.mois.map(m => {
              const maxVal = Math.max(...annuelEmployes.mois.map(x => x.total), 1);
              const barH = m.total > 0 ? Math.max(8, (m.total / maxVal) * 100) : 0;
              const isActive = m.mois === currentMonth;
              return (
                <button key={m.mois} onClick={() => setCurrentMonth(m.mois)} className="flex-1 flex flex-col items-center justify-end gap-1 group cursor-pointer" data-testid={`emp-month-btn-${parseInt(m.mois.split('-')[1])}`}>
                  {m.total > 0 && (
                    <span className="text-[8px] font-mono text-[rgb(var(--lm-text-muted))] opacity-0 group-hover:opacity-100 transition-opacity">
                      {m.total >= 1000 ? `${(m.total / 1000).toFixed(1)}k` : Math.round(m.total)}
                    </span>
                  )}
                  <div className="w-full rounded-t-sm transition-all duration-300" style={{
                    height: `${barH}%`,
                    minHeight: m.total > 0 ? '4px' : '0',
                    background: isActive ? 'rgb(249, 115, 22)' : m.total > 0 ? 'rgba(249, 115, 22, 0.3)' : 'rgb(var(--lm-surface-elevated) / 0.3)',
                  }} />
                  <span className={`text-[8px] uppercase ${isActive ? 'font-medium' : ''}`} style={{ color: isActive ? 'rgb(249, 115, 22)' : 'rgb(var(--lm-text-muted))' }}>
                    {m.mois_label.slice(0, 1)}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Resume par employe annuel */}
          {annuelEmployes.par_employe?.length > 0 && (
            <div className="border-t border-[rgb(var(--lm-border)/0.3)] pt-3">
              <p className="text-[8px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-2">Par employe</p>
              <div className="space-y-1.5">
                {annuelEmployes.par_employe.map(e => {
                  const pctEmp = annuelEmployes.total_annee > 0 ? (e.total / annuelEmployes.total_annee * 100) : 0;
                  return (
                    <div key={e.employe} className="flex items-center gap-2" data-testid={`emp-annuel-${e.employe}`}>
                      <span className="text-xs text-[rgb(var(--lm-text-secondary))] flex-1">{e.employe}</span>
                      <div className="w-24 h-1.5 bg-[rgb(var(--lm-surface-elevated))] rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-orange-400 transition-all" style={{ width: `${pctEmp}%` }} />
                      </div>
                      <span className="font-mono text-xs text-orange-400 w-20 text-right">{formatCurrency(e.total)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SummaryRow = ({ label, value, positive, highlight }) => (
  <div className="flex items-center justify-between">
    <span className={`text-xs ${highlight ? 'font-medium' : ''}`} style={highlight ? { color: 'rgb(var(--lm-gold))' } : { color: 'rgb(var(--lm-text-secondary))' }}>
      {label}
    </span>
    <span className={`font-mono text-sm ${highlight ? 'font-medium' : ''} ${value >= 0 ? (positive || highlight ? '' : 'text-[rgb(var(--lm-text))]') : 'text-[rgb(var(--lm-red))]'}`}
      style={value >= 0 && (positive || highlight) ? { color: highlight ? 'rgb(var(--lm-gold))' : 'rgb(var(--lm-green))' } : {}}>
      {value >= 0 ? (positive ? '+' : '') : ''}{formatCurrency(value)}
    </span>
  </div>
);

export default Perso;
